/**
 * EntityChip: a metadata name in a row (outcome card, plan list) as the one chip the prose uses (UX map C16 / 5.7).
 * In this task's package with a diff → a button that opens the diff in the panel; any other name → "Open in explorer"
 * (a real link to `/studio/metadata?node=Type:Name`, a new tab). Replaces the separate "View diff" column.
 */
import { cn } from "@/lib/utils";
import { chipActionOf, type ChipTarget } from "@/services/multi-agent/laneV2/entityChips";

const CHIP_CLASS =
  "ma-entity-link min-w-0 max-w-full cursor-pointer truncate text-left font-mono text-[12px] font-medium text-[var(--ma-accent-text)] underline decoration-dotted underline-offset-2 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function EntityChip({
  target,
  onOpenDiff,
  className,
}: {
  target: ChipTarget;
  /** Open the diff of a package member. Omitted, a diff target falls back to the explorer link. */
  onOpenDiff?: (taskId: string, workItemId: string) => void;
  className?: string;
}) {
  const action = chipActionOf(target);
  const title = `${target.metadataType} · ${target.fullName}`;
  // A brand-new component with no diff yet has nowhere to open: plain text, not a dead link.
  if (!action || (action.kind === "diff" && !onOpenDiff)) {
    return (
      <span title={title} className={cn("min-w-0 max-w-full truncate font-mono text-[12px] text-foreground", className)}>
        {target.fullName}
      </span>
    );
  }
  if (action.kind === "diff") {
    return (
      <button
        type="button"
        onClick={() => onOpenDiff?.(action.taskId, action.workItemId)}
        title={`${title} · open the changes`}
        data-lane-v2-chip="diff"
        data-lane-v2-view-diff={action.workItemId}
        className={cn(CHIP_CLASS, className)}
      >
        {target.fullName}
      </button>
    );
  }
  return (
    <a
      href={action.href}
      target="_blank"
      rel="noopener noreferrer"
      title={`${title} · open in explorer`}
      data-lane-v2-chip="explorer"
      className={cn(CHIP_CLASS, className)}
    >
      {target.fullName}
      <span aria-hidden> {"↗"}</span>
      <span className="sr-only"> (open in explorer)</span>
    </a>
  );
}
